import { makeAutoObservable, runInAction } from "mobx";
import { NavigateFunction } from "react-router";

export type RouteParams =
  | Record<string, string | number | undefined | boolean>
  | undefined;
export type QueryParams = RouteParams;

export class RouterStoreAdapter {
  params: RouteParams = {};
  queryParams: QueryParams = {};
  currentPath: string = "";
  private navigateFunc: NavigateFunction | null = null;
  private rootStore: any = null;

  constructor() {
    makeAutoObservable(this);
  }

  setRootStore(rootStore: any) {
    this.rootStore = rootStore;
  }

  updateFromReactRouter(
    params: RouteParams,
    pathname: string,
    search: string,
    navigate: NavigateFunction
  ) {
    const previousPathname = this.rootStore?.location?.pathname;

    this.params = params;
    this.queryParams = Object.fromEntries(new URLSearchParams(search));
    this.currentPath = pathname + search;
    this.navigateFunc = navigate;

    if (this.rootStore) {
      if (
        previousPathname &&
        previousPathname !== pathname &&
        this.rootStore.drawerViewOpen
      ) {
        this.rootStore.closeDrawerView();
      }

      this.rootStore.location = { pathname, search };
    }
  }

  goTo(
    _route: any,
    params?: RouteParams,
    queryParams?: QueryParams,
    state?: any,
    breadcrumbLabel?: string
  ) {
    if (!this.navigateFunc) {
      console.error("Navigate function not initialized");
      return;
    }

    let path = _route?.path || _route;

    if (typeof path === "string" && params) {
      path = path.replace(/:(\w+)\??/g, (match, key) => {
        const value = params[key as keyof typeof params];
        if (value !== undefined && value !== null) {
          return String(value);
        }
        return match.includes("?") ? "" : match;
      });
    }

    const search = queryParams
      ? "?" +
        new URLSearchParams(queryParams as Record<string, string>).toString()
      : "";

    if (this.rootStore) {
      if (breadcrumbLabel) {
        this.rootStore.pushBreadcrumb(breadcrumbLabel, this.pathname);
      } else {
        this.rootStore.clearBreadcrumbs();
      }
    }

    this.navigateFunc(path + search, { state });
  }

  replace(path: string, state?: any) {
    if (!this.navigateFunc) {
      console.error("Navigate function not initialized");
      return;
    }
    this.navigateFunc(path, { replace: true, state: state || {} });
  }

  goBack(count: number = 1) {
    if (!this.navigateFunc) {
      console.error("Navigate function not initialized");
      return;
    }
    this.navigateFunc(-count);
  }

  /**
   * Si hay una página anterior de la aplicación a la que volver. Una pestaña
   * nueva (abierta desde un enlace) empieza sin ninguna: ahí volver atrás no
   * navega. El router guarda en history.state la posición de cada entrada (idx).
   */
  canGoBack(): boolean {
    const idx =
      typeof window === "undefined" ? undefined : window.history.state?.idx;
    return typeof idx === "number" && idx > 0;
  }

  go(distance: number) {
    if (!this.navigateFunc) {
      console.error("Navigate function not initialized");
      return;
    }
    runInAction(() => {
      this.navigateFunc!(distance);
    });
  }

  get pathname(): string {
    const idx = this.currentPath.indexOf("?");
    return idx >= 0 ? this.currentPath.substring(0, idx) : this.currentPath;
  }

  get search(): string {
    const idx = this.currentPath.indexOf("?");
    return idx >= 0 ? this.currentPath.substring(idx) : "";
  }
}
